import React, {useEffect} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import {useSelector} from "react-redux";
import {useAuthContext} from "Context/authContext";
import {selectSubscriptionInfo, selectIsSubscriptionLoaded} from "store/reducers/subscriptionSlice";
import Toast from "components/molecules/Toast";
import Loaders from "components/atoms/Loaders";

const SubscriptionGuard = ({children}) => {
    const {isLoggedIn, loading_user} = useAuthContext();
    const navigate = useNavigate();
    const location = useLocation();
    const subscriptionInfo = useSelector(selectSubscriptionInfo);
    const isSubscriptionLoaded = useSelector(selectIsSubscriptionLoaded);
    const hasSubscriptionOverride = localStorage.getItem("subscription_override") === "true";

    // List of pages that don't require subscription
    const publicPages = ["/payment", "/billing/success", "/business-setup", "/thank-you", "/pricing"];

    // Check if current page requires subscription
    const requiresSubscription = !publicPages.some(page => location.pathname.startsWith(page));

    // Special handling for pricing page
    const isPricingPage = location.pathname === "/pricing";

    // Determine if user has active subscription from Redux data
    const hasActiveSubscription = !subscriptionInfo.subscriptionRequired || subscriptionInfo.canAccess;

    useEffect(() => {
        // Only handle logged in users (PrivateRoute handles logged out users)
        if (isLoggedIn && !loading_user && isSubscriptionLoaded) {
            // Add small delay to prevent race conditions during signup flow
            const timer = setTimeout(() => {
                handleSubscriptionStatus(hasActiveSubscription || hasSubscriptionOverride);
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [
        isLoggedIn,
        loading_user,
        isSubscriptionLoaded,
        subscriptionInfo.canAccess,
        subscriptionInfo.subscriptionRequired,
        location.pathname,
    ]);

    // Helper function to handle subscription status logic
    const handleSubscriptionStatus = isActive => {
        if (isActive) {
            // If user has subscription but is on pricing page, redirect to dashboard
            if (isPricingPage) {
                navigate("/dashboard");
            }
        } else {
            // User doesn't have active subscription
            if (requiresSubscription && !isPricingPage) {
                // Use custom message from subscription data if available
                const message = subscriptionInfo.message || "Please subscribe to a plan to access this feature";

                Toast({
                    type: "warning",
                    message: message,
                });

                // Navigate to pricing with business info if available
                const businessId = subscriptionInfo.businessId || "";
                const businessName = subscriptionInfo.businessName || "";

                if (businessId && businessName) {
                    navigate(`/pricing?businessId=${businessId}&businessName=${encodeURIComponent(businessName)}`);
                } else {
                    navigate("/pricing");
                }
            } else if (isPricingPage) {
                // Allow access to pricing page for users without subscription
                console.log("SubscriptionGuard: No subscription, allowing access to pricing page");
            }
        }
    };

    // Show loading while checking user status or waiting for subscription data to load
    if (loading_user || (isLoggedIn && !isSubscriptionLoaded)) {
        return (
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "100vh",
                    width: "100%",
                }}>
                <Loaders viewLoader />
            </div>
        );
    }

    // If logged in but no subscription and trying to access protected page (unless override is active)
    if (
        isLoggedIn &&
        requiresSubscription &&
        !isPricingPage &&
        !hasActiveSubscription &&
        !hasSubscriptionOverride &&
        isSubscriptionLoaded
    ) {
        return null; // Will redirect via useEffect
    }

    // Allow access
    return children;
};

export default SubscriptionGuard;
